import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './Appointment.module.css';
import CustomButton from "../../components/buttons/CustomButton";
import IconDoc from '@mui/icons-material/Description';
import IconPrescription from '@mui/icons-material/MedicationOutlined';
import IconStart from '@mui/icons-material/PlayArrow';
import IconStop from '@mui/icons-material/StopSharp';
import IconMoney from '@mui/icons-material/MonetizationOn';
import IconSave from '@mui/icons-material/Save';
import AccessDenied from '../../components/access-denied/AccessDenied';
import AppointmentLockedPreview from './components/lockedPreview/AppointmentLockedPreview';
import { HEALTH_PROFESSIONAL_LABEL } from '../../constants/userRoles';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { SetPatientComplaints, UpdateAppointmentStatusAPI, GetAppointmentFullInfo, GetInvoiceAPI } from '../../api/API';
import { DataProviderInstance } from "../../api/DataProvider";
import AppointmensState from '../../consts/AppointmentsState';
import Loading from '../../components/loading/Loading';
import AppointmentInfo from './components/appointmentInfo/AppointmentInfo';
import PatientInfo from './components/patientInfo/PatientInfo';
import AppointmentModules from './components/appointmentModules/AppointmentModules';
import FullReportButton from './components/fullReport/FullReportButton';
import CalendarConfig from '../../consts/CalendarConfig';
import { MODULE_KEYS, findAppointmentNote, parseComplaintsList, resolveVisibleModules, splitModules } from './modules/appointmentModules';
import AppointmentDoneConfirmPopupContent from '../../popup/AppointmentDoneConfirmPopupContent/AppointmentDoneConfirmPopupContent';
import CreateInvoicePopup from '../../popup/CreateInvoicePopup/CreateInvoicePopup';
import FiscalInvoicePopup from '../../popup/FiscalInvoicePopup/FiscalInvoicePopup';
import CreateDocumentPopup from '../../popup/CreateDocumentPopup/CreateDocumentPopup';
import PrescriptionPopup from '../../popup/PrescriptionPopup/PrescriptionPopup';
import AppointmentTypePopup from '../../popup/AppointmentTypePopup/AppointmentTypePopup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

function Appointment() {
  const statusNotInitialized = 'notInitialized';
  const statusInitialized = 'initialized';
  const statusFinished = 'finished';
  const statusRegistered = 'registered';

  // Todos os hooks devem ser chamados incondicionalmente no início do componente
  const navigate = useNavigate();
  const { canAccessHealthData } = useRoleAccess();
  const { id } = useParams();
  const { SetPatientHistory, SetPatientNote, AnnulPatientHistoryRecord, CreateReplacementHistoryRecord, CompanyConfig, GetPatientAttachmentFile } = DataProviderInstance();
  
  // Todos os useState hooks
  const [data, setData] = useState(null);
  // Módulos fora do tipo abertos pelo atalho "Outros registros deste paciente"
  const [openedModules, setOpenedModules] = useState([]);
  const [accessError, setAccessError] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(statusNotInitialized);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [todayAssessment, setTodayAssessment] = useState("");
  const [todayEvolution, setTodayEvolution] = useState("");
  const [todayNote, setTodayNote] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [intervalId, setIntervalId] = useState(null);
  const [confirmPopupVisibility, setConfirmPopupVisibility] = useState(false);
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [showTypePopup, setShowTypePopup] = useState(false);
  const [isChangingType, setIsChangingType] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [showCreateDocumentPopup, setShowCreateDocumentPopup] = useState(false);
  const [showPrescriptionPopup, setShowPrescriptionPopup] = useState(false);
  const [showFiscalInvoicePopup, setShowFiscalInvoicePopup] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [invoicePrice, setInvoicePrice] = useState(null);

  // Início do cronômetro guardado por agendamento (como os rascunhos `${id}_note`)
  const startTimeKey = `${id}_startTime`;

  // useRef hooks
  const entryRef = useRef(null);
  // Atendimento já carregado ao abrir: o StrictMode roda o efeito duas vezes
  // e cada carga grava uma leitura do prontuário na auditoria.
  const loadedIdRef = useRef(null);

  // Tipos de atendimento ativos da clínica (CompanyConfig) e o tipo em vigor nesta tela:
  // o gravado no agendamento ou, em registros antigos sem tipo, o sugerido (último usado
  // com o paciente, senão o padrão da clínica). Antes de iniciar não há tipo: a tela
  // mostra só o agendamento, o paciente e os registros que ele já tem; o tipo é
  // escolhido ao clicar em "Iniciar", com o sugerido pré-selecionado.
  const appointmentTypes = CompanyConfig?.AppointmentTypes || [];
  const findType = (typeId) => appointmentTypes.find((type) => String(type.Id) === String(typeId)) || null;
  const lastUsedType = findType(data?.lastAppointmentTypeId);
  const suggestedType = lastUsedType || appointmentTypes.find((type) => type.IsDefault) || appointmentTypes[0] || null;
  const effectiveType = data?.appointmentType || (currentStatus == statusNotInitialized ? null : suggestedType);
  // Sem tipo em vigor (antes de iniciar) a tela mostra todos os registros do
  // paciente; com tipo, só o tipo e o que foi registrado nesta consulta, e o
  // histórico dos demais módulos fica a um clique (otherModules).
  const moduleSplit = effectiveType
    ? splitModules(effectiveType.Modules, data, data?.appointment?.id)
    : { visible: resolveVisibleModules([], data), others: [] };
  const visibleModules = [...moduleSplit.visible, ...openedModules.filter((key) => !moduleSplit.visible.includes(key))];
  const otherModules = moduleSplit.others.filter((item) => !openedModules.includes(item.key));
  const requiresEvolution = visibleModules.includes(MODULE_KEYS.EVOLUTION);
  const hasNotesModule = visibleModules.includes(MODULE_KEYS.NOTES);

  // useEffect hooks
  useEffect(() => {
    if (loadedIdRef.current === id) return;
    loadedIdRef.current = id;
    setOpenedModules([]);
    LoadData();
    // No timeout to clear on unmount
    return () => { };
  }, [id]);

  useEffect(() => {
    if (currentStatus == statusInitialized) {
      StartTimer();
    }
    else {
      StopTimer();
    }
  }, [currentStatus]);

  // Verificar acesso após todos os hooks: atendimento é dado de saúde, pede a marcação
  const hasAccess = canAccessHealthData();
  
  // Se não tiver acesso, retornar imediatamente o componente AccessDenied
  if (!hasAccess) {
    return (
      <AccessDenied
        title="O atendimento abre o prontuário"
        description={`Por isso ele aparece só para quem tem a marcação ${HEALTH_PROFESSIONAL_LABEL} no usuário.`}
        hint="Peça a quem é proprietário(a) da conta para ligar a marcação no seu usuário, em Usuários."
        preview={<AppointmentLockedPreview />}
      />
    );
  }
  function LoadData() {
    // Verificar se o usuário tem acesso antes de fazer qualquer chamada de API
    if (!hasAccess) {
      // Se não tiver acesso, não fazer nada
      return;
    }
    
    setIsLoading(true);
    setAccessError(null);

    // We no longer need a timeout for error handling

    try {
      // Direct error handling to ensure we don't get stuck in loading
      GetAppointmentFullInfo(
        id,
        (response) => {
          

          // Check if the response is an error response
          if (response && response.error) {
            
            setAccessError({ message: 'Agendamento não encontrado' });
            setIsLoading(false);
            return;
          }

          // Check if response is empty or invalid
          if (!response || !response.appointment) {
            
            setAccessError({ message: 'Agendamento não encontrado' });
            setIsLoading(false);
            return;
          }

          // Process successful response
          
          setAccessError(null);
          let initialComplaints = [];

          // Safe access to complaints with null checks
          if (response.complaints && Array.isArray(response.complaints) && response.complaints.length > 0) {
            try {
              const complaintsData = response.complaints[0]?.list;
              if (complaintsData && typeof complaintsData === 'string') {
                initialComplaints = JSON.parse(complaintsData);
                setComplaints(initialComplaints);
              }
            } catch (error) {
              setComplaints([]);
            }
          }

          // Safe access to appointment status with null checks
          if (response.appointment && response.appointment.status !== undefined) {
            if (response.appointment.status == AppointmensState.Doing) {
              if (localStorage.getItem(startTimeKey) == null) {
                localStorage.setItem(startTimeKey, Date.now());
              }
              setCurrentStatus(statusInitialized);
            }
            else if (response.appointment.status == AppointmensState.Done) {
              setCurrentStatus(statusRegistered);

              // Safe access to invoiceId with default value
              if (response.appointment.invoiceId && response.appointment.invoiceId != 0) {
                setInvoiceCreated(true);
                // Buscar o valor real do invoice para usar na nota fiscal
                GetInvoiceAPI(response.appointment.invoiceId, (invoiceResponse) => {
                  if (invoiceResponse && invoiceResponse.invoice && invoiceResponse.invoice.price) {
                    setInvoicePrice(invoiceResponse.invoice.price);
                  }
                });
              }
            }
          }

          setData(response);
          setIsLoading(false);
        },
        (error, statusCode) => {
          
          
          // Handle different error formats
          setAccessError({ message: 'Agendamento não encontrado' });
          setIsLoading(false);

        }
      );
    } catch (e) {
      // Catch any unexpected errors to prevent getting stuck in loading
      
      setAccessError({ message: 'Agendamento não encontrado' });
      setIsLoading(false);
    }
  }

  function OnStartHandler() {
    // Com um único tipo ativo não há o que escolher: inicia direto
    if (appointmentTypes.length <= 1) {
      startAppointmentWithType(appointmentTypes[0]?.Id || null);
      return;
    }
    setSelectedTypeId(suggestedType?.Id || null);
    setIsChangingType(false);
    setShowTypePopup(true);
  }

  // Troca de tipo durante o atendimento (lápis no cabeçalho)
  function openTypePopup() {
    setSelectedTypeId(effectiveType?.Id || null);
    setIsChangingType(true);
    setShowTypePopup(true);
  }

  // Reflete o tipo na tela sem recarregar: os cards seguem a nova lista de módulos
  function applyAppointmentType(typeId) {
    const type = findType(typeId);
    setData((previous) => ({
      ...previous,
      appointment: { ...previous.appointment, appointmentTypeId: typeId != null ? String(typeId) : null },
      appointmentType: type,
    }));
  }

  function startAppointmentWithType(typeId) {
    setShowTypePopup(false);

    if (!data?.appointment?.id) {
      return;
    }

    UpdateAppointmentStatusAPI(data.appointment.id, AppointmensState.Doing, (response) => { }, typeId);
    applyAppointmentType(typeId);
    localStorage.setItem(startTimeKey, Date.now());
    setCurrentStatus(statusInitialized);
  }

  function changeAppointmentType(typeId) {
    setShowTypePopup(false);

    if (!data?.appointment?.id || typeId == null) {
      return;
    }

    // O status no servidor continua "em atendimento"; só o tipo muda
    UpdateAppointmentStatusAPI(data.appointment.id, AppointmensState.Doing, (response) => { }, typeId);
    applyAppointmentType(typeId);
  }

  function onTypePopupConfirm() {
    if (isChangingType) {
      changeAppointmentType(selectedTypeId);
    } else {
      startAppointmentWithType(selectedTypeId);
    }
  }

  function GetElapsedTime() {
    const startTime = localStorage.getItem(startTimeKey);
    if (!startTime) {
      return 0;
    }
    
    const startTimeNum = parseInt(startTime, 10);
    if (isNaN(startTimeNum)) {
      return 0;
    }
    
    const currentTime = Date.now();
    const timeDifferenceInSeconds = Math.floor((currentTime - startTimeNum) / 1000);

    return Math.max(0, timeDifferenceInSeconds);
  }

  function StartTimer() {

    setCurrentSeconds(GetElapsedTime());

    let interval = setInterval(() => {
      setCurrentSeconds(GetElapsedTime());
    }, 1000);

    setIntervalId(interval);
  }

  function StopTimer() {
    clearInterval(intervalId);
    setIntervalId(null);
  }

  function ResumeTimer() {
    // Change status back to initialized to restart the timer
    setCurrentStatus(statusInitialized);
    // Timer will automatically start due to the useEffect
  }

  // "Finalizar e registrar": para o cronômetro e já abre o resumo para confirmar.
  // Se a pessoa fechar o resumo, fica em "Falta registrar" com Retomar e Registrar.
  function OnFinishHandler() {
    StopTimer();
    setCurrentStatus(statusFinished);
    OpenConfirmationPopup();
  }

  function OnSaveHandler() {
    // Directly open confirmation popup without checking fields
    // The validation will happen in the confirmation popup
    OpenConfirmationPopup();
  }

  // Function to handle dialog close
  const handleCloseDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  function RegisterAppointmentHandler() {
    RegisterAppointment();
  }

  function RegisterAppointment() {
    // Safe access to data with null checks
    if (!data?.appointment?.id || !data?.patient?.id || !data?.appointment?.date) {
      return;
    }

    let assessment = todayAssessment || '';
    let evolution = todayEvolution || '';

    const previousComplaints = JSON.stringify(parseComplaintsList(data));
    const currentComplaints = JSON.stringify(complaints || []);

    ClearAll();

    const saveComplaints = (response) => {
      if (previousComplaints != currentComplaints) {
        SetPatientComplaints(data.appointment.date, data.patient.id, data.appointment.id, complaints || [], (response) => {
          OnAllDataSaved(response);
        });
      }
      else {
        OnAllDataSaved(response);
      }
    };

    const saveHistory = (response) => {
      // Sem o módulo de queixa/evolução no tipo não há registro clínico a gravar
      if (!requiresEvolution) {
        saveComplaints(response);
        return;
      }
      SetPatientHistory(data.appointment.date, data.patient.id, data.appointment.id, assessment, evolution, saveComplaints);
    };

    // Anotação do atendimento: só grava se mudou em relação ao que já está salvo
    const savedNote = findAppointmentNote(data.notes, data.appointment.id)?.note || '';
    if (hasNotesModule && (todayNote || '').trim() !== savedNote.trim()) {
      SetPatientNote(data.patient.id, data.appointment.id, todayNote || '', saveHistory);
      return;
    }

    saveHistory(null);
  }

  function OpenConfirmationPopup() {
    setConfirmPopupVisibility(true);
  }

  function OnInvoiceHandler() {
    setShowInvoicePopup(true);
  }

  function onChangeComplaintsHandler(values) {

    setComplaints(values);
  }

  function ClearAll() {
    setCurrentSeconds(0);
    //inputAssessmentRef.current.Clear();
    //inputEvolutionRef.current.Clear();
  }

  function OnAllDataSaved(response) {
    // Safe access to entryRef
    if (entryRef.current && typeof entryRef.current.Clear === 'function') {
      entryRef.current.Clear();
    }
    
    setCurrentStatus(statusRegistered);
    localStorage.removeItem(startTimeKey);
    
    // Safe access to appointment data
    if (data?.appointment?.id) {
      UpdateAppointmentStatusAPI(data.appointment.id, AppointmensState.Done, (response) => {
        ReloadPage();
      });
    } else {
      // Cannot update appointment status: appointment ID is missing
    }
  }

  function ReloadPage() {
    LoadData();
  }

  function GoToAppointmentsScreen() {
    navigate("/schedule/appointments");
  }

  function GetCurrentButton() {

    if (currentStatus == statusNotInitialized) {
      return (
        <CustomButton
          variant="contained"
          style="secondary"
          label={"Iniciar"}
          data-tour="appointment-start"
          icon={<IconStart />}
          onClick={OnStartHandler}
          fullWidth={false}
        />
      );
    } else if (currentStatus == statusInitialized) {
      return (
        <CustomButton
          variant="contained"
          style="secondary"
          label={"Finalizar e registrar"}
          data-tour="appointment-finish"
          // Guia: com o módulo de queixa e evolução, o botão só é sugerido com os dois preenchidos
          data-tour-ready={!requiresEvolution || (todayAssessment && todayEvolution) ? '1' : '0'}
          icon={<IconStop />}
          onClick={OnFinishHandler}
          fullWidth={false}

        />)
    } else if (currentStatus == statusFinished) {
      return (
        <div className={styles.headerButtons}>
          <CustomButton
            variant="outlined"
            style="primary"
            label={"Retomar"}
            data-tour="appointment-resume"
            icon={<IconStart />}
            onClick={ResumeTimer}
            fullWidth={false}
          />
          <CustomButton
            variant="contained"
            style="secondary"
            label={"Registrar"}
            data-tour="appointment-register"
            icon={<IconSave />}
            onClick={OnSaveHandler}
            fullWidth={false}
          />
        </div>
      )
    }
    else if (currentStatus == statusRegistered) {
      return (
        <div className={styles.headerButtons}>
          <CustomButton
            variant="outlined"
            style="primary"
            label={"Emitir documento"}
            icon={<IconDoc />}
            onClick={OnCreateDocumentHandler}
            fullWidth={false}
          />
          <CustomButton
            variant="outlined"
            style="primary"
            label={"Emitir receita"}
            icon={<IconPrescription />}
            onClick={() => setShowPrescriptionPopup(true)}
            fullWidth={false}
          />

          {invoiceCreated ? (
            <CustomButton
              variant="contained"
              style="secondary"
              label={"Nota Fiscal (NFS-e)"}
              icon={<IconMoney />}
              onClick={() => setShowFiscalInvoicePopup(true)}
              fullWidth={false}
            />
          ) : (
            <CustomButton
              variant="contained"
              style="secondary"
              label={"Faturar atendimento"}
              data-tour="appointment-invoice"
              icon={<IconMoney />}
              onClick={OnInvoiceHandler}
              fullWidth={false}
            />
          )}
        </div>
      )
    }
  }

  function onAssesmentChangeHandler(value) {
    setTodayAssessment(value);
  }

  function onEvolutionChangeHandler(value) {
    setTodayEvolution(value);
  }

  function onNoteChangeHandler(value) {
    setTodayNote(value);
  }

  function OnInvoiceCreatedHandler() {
    setInvoiceCreated(true);
    ReloadPage();
  }

  function OnInvoiceViewHandler() {
    if (data?.appointment?.invoiceId) {
      navigate("/invoice/" + data.appointment.invoiceId);
    } else {
      // Cannot view invoice: invoice ID is missing
    }
    //window.open(APPOINTMENT_GET_RECEIPT + "?id=" + data.appointment.id, "_blank");
  }

  function OnCreateDocumentHandler() {
    setShowCreateDocumentPopup(true);
    /*navigate("/document/" + data.appointment.id);*/
  }

  // Extracted error message component with clean design
  const ErrorMessage = () => (
    <div className='page-content'>
      <div className="error-container" style={{
        padding: '30px',
        textAlign: 'center',
        maxWidth: '500px',
        margin: '60px auto',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          fontSize: '22px',
          fontWeight: '500',
          color: '#333333',
          marginBottom: '15px'
        }}>
          Agendamento não encontrado
        </div>
        <div style={{
          fontSize: '14px',
          color: '#666666',
          marginBottom: '20px'
        }}>
          {""}
        </div>
      </div>
    </div>
  );

  if (accessError) {
    
    return <ErrorMessage />;
  }
  else if (data == null && isLoading) {
    
    return (
      <div className='page-content'>
        <Loading />
      </div>
    )
  }
  else if (data == null && !isLoading) {
    
    return <ErrorMessage />;
  }
  else {

    let enabled = currentStatus != statusNotInitialized && currentStatus != statusRegistered;
    let initialComplaints = parseComplaintsList(data);

    // O tipo só pode ser trocado com o atendimento em andamento e se houver mais de um
    const canChangeType = enabled && appointmentTypes.length > 1;

    // Relatório completo: todos os módulos com registro, na ordem dos cards
    const reportContext = {
      data,
      patient: data?.patient || {},
      appointment: data?.appointment || null,
      companyConfig: CompanyConfig,
      complaints,
      loaders: { GetPatientAttachmentFile },
    };
    const appointmentDate = data?.appointment?.date ? CalendarConfig.GetDDMMAAAA(data.appointment.date) : '';
    const reportButton = (
      <FullReportButton
        modules={visibleModules}
        ctx={reportContext}
        patientName={data?.patient?.name}
        professionalId={data?.appointment?.resource}
        companyConfig={CompanyConfig}
        title="Relatório do atendimento"
        subtitle={[appointmentDate ? `Atendimento de ${appointmentDate}` : '', effectiveType?.Label].filter(Boolean).join(' · ')}
        date={new Date().toLocaleDateString('pt-BR')}
      />
    );

    let appointmentDoneData = {
      duration: currentSeconds || 0,
      // Início real do cronômetro (quando clicou em "Iniciar"), para o resumo mostrar o horário de verdade
      startedAt: parseInt(localStorage.getItem(startTimeKey), 10) || null,
      appointment: data?.appointment || {},
      patient: data?.patient || {},
      complaints: complaints || [],
      todayAssessment: todayAssessment || '',
      todayEvolution: todayEvolution || '',
      requiresEvolution: requiresEvolution,
    };

    return (
      <div className='page-content'>
        <div className={styles.page}>
          {/* Cabeçalho: card do paciente e card do atendimento, lado a lado */}
          <div className={styles.header}>
            <div className={`${styles.headerCard} ${styles.patientCard}`}>
              <PatientInfo data={data?.patient || {}} visits={data?.patientVisits} />
            </div>
            <div className={styles.headerCard}>
              <AppointmentInfo
                data={data?.appointment || {}}
                enabled={enabled}
                elapsedTime={currentSeconds || 0}
                button={GetCurrentButton()}
                reportButton={reportButton}
                appointmentTypeLabel={effectiveType?.Label}
                onChangeTypeClick={canChangeType ? openTypePopup : null}
                status={currentStatus}
              />
            </div>
          </div>

          {/* Cards dos módulos do tipo de atendimento (mais os registrados nesta consulta) */}
          <AppointmentModules
            modules={visibleModules}
            data={data}
            enabled={enabled}
            initialComplaints={initialComplaints}
            onComplaintsChange={onChangeComplaintsHandler}
            entryRef={entryRef}
            onAssessmentChange={onAssesmentChangeHandler}
            onEvolutionChange={onEvolutionChangeHandler}
            onReload={ReloadPage}
            onNoteChange={onNoteChangeHandler}
            companyConfig={CompanyConfig}
            appointmentType={effectiveType}
            otherModules={otherModules}
            onOpenModule={(key) => setOpenedModules((previous) => (previous.includes(key) ? previous : [...previous, key]))}
          />
        </div>

        {confirmPopupVisibility && <AppointmentDoneConfirmPopupContent Data={appointmentDoneData} OnSave={RegisterAppointmentHandler} OnHide={() => setConfirmPopupVisibility(false)} />}
        {showFiscalInvoicePopup && (
          <FiscalInvoicePopup
            appointmentData={{
              ...(data?.appointment || {}),
              price: invoicePrice !== null ? invoicePrice : data?.appointment?.price,
            }}
            patientData={data?.patient || {}}
            onHide={() => setShowFiscalInvoicePopup(false)}
          />
        )}
        {showInvoicePopup && <CreateInvoicePopup Data={data?.appointment || {}} OnHide={(needsReload) => {

          setShowInvoicePopup(false);
          if (needsReload) {
            OnInvoiceCreatedHandler();
          }

        }} />}
        {showCreateDocumentPopup && <CreateDocumentPopup Data={data?.appointment || {}} Diagnoses={data?.diagnoses} OnHide={(needsPageReload) => {
          setShowCreateDocumentPopup(false);
          if (needsPageReload) {
            ReloadPage();
          }
        }} />}
        {showPrescriptionPopup && (
          <PrescriptionPopup
            patientId={data?.patient?.id}
            patientName={data?.patient?.name}
            appointmentId={data?.appointment?.id}
            appointmentDate={data?.appointment?.date || ''}
            professionalId={data?.appointment?.resource}
            prescription={null}
            templates={data?.prescriptionTemplates || []}
            OnHide={(needsPageReload) => {
              setShowPrescriptionPopup(false);
              if (needsPageReload) {
                ReloadPage();
              }
            }}
          />
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Cancelar
            </Button>
            <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Escolha do tipo de atendimento */}
        {showTypePopup && (
          <AppointmentTypePopup
            types={appointmentTypes}
            selectedTypeId={selectedTypeId}
            onSelect={setSelectedTypeId}
            onConfirm={onTypePopupConfirm}
            onClose={() => setShowTypePopup(false)}
            isChangingType={isChangingType}
            lastTypeId={lastUsedType?.Id || null}
          />
        )}
      </div>
    );
  }

}

export default Appointment;
